// @dynamic-i18n-keys
//   coreBadge.awardBadge.success.tooltip.deviation
//   coreBadge.awardBadge.success.tooltip.comment
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Notification, { NotificationDuration } from '../../Notifications/Notification';
import SystemNotification from '../../SystemNotification';
import { ResourceType } from '../../types/resource';
import { AwardBadgeSource, BadgeNotification as BadgeNotificationType } from '../types';
import { scrollIntoViewInViewportBottom } from '../../utils/scrollIntoViewWithHeader';
import BadgeNotificationIcon from './BadgeNotificationIcon';

import s from './BadgeNotification.scss';

type Params = BadgeNotificationType['params'] & Omit<BadgeNotificationType, 'params'>;

export interface Props extends Params {
  removeNotification: (id: number) => void;
}

const BadgeNotification: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  queueName,
  badge,
  recipient,
  source,
  removeNotification,
}) => {
  const isMobile = useContext(MobileContext);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  let transKey;
  switch (recipient.typeId) {
    case ResourceType.DEVIATION:
      transKey = 'coreBadge.awardBadge.success.tooltip.deviation';
      break;

    case ResourceType.COMMENT:
      transKey = 'coreBadge.awardBadge.success.tooltip.comment';
      break;

    default:
      return null;
  }

  return (
    <Notification
      id={id}
      queueName={queueName}
      cardComponent={SystemNotification}
      onOpen={(notificationId, el) => {
        if (
          !isOpen &&
          recipient.typeId === ResourceType.DEVIATION &&
          source !== AwardBadgeSource.THUMB_OVERLAY
        ) {
          // Scroll so that the badge itself is also in view
          setIsOpen(true);
          el && scrollIntoViewInViewportBottom(el, isMobile, 60, undefined);
        }
      }}
      onClose={() => {
        setIsOpen(false);
        removeNotification(id);
      }}
      cardProps={{
        multiLine: true,
        duration: NotificationDuration.M,
        withCloseButton: !isMobile,
        withShadow: false,
        closeButtonClassName:
          source === AwardBadgeSource.THUMB_OVERLAY ? undefined : s['close-button'],
        className: s['root'],
        customIcon: <BadgeNotificationIcon name={badge.name} imgSrc={badge.images[0].imageUrl} />,
      }}
    >
      <div className={s['badge-text']}>
        {t(transKey, {
          postProcess: ['reactPostprocessor', 'featureBrancher'],
          title: badge.baseTitle,
          titlebold: <strong>{badge.baseTitle}</strong>,
        })}
      </div>
    </Notification>
  );
};
BadgeNotification.displayName = 'BadgeNotification';

export default BadgeNotification;
